import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { Application, Company, CompanyApplication, Version } from '../../../../../interfaces/users.interface';
import { HardeningDocument } from '../../../../../interfaces/hardeningDocuments';
import { CompanyTabListParams } from '../../../../../interfaces/companyDetail.interface';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { fetchDocument } from '../../../../../app/store/serviceCategories';
import { getCompanyApps } from '../../../../../services/companyDetail';
import { useCompanyResource } from '../../../../Vendors/useCompanyResource';
import { DataCell, DataRow } from '../../../../Tables/DataList';
import { PaginationBar, DEFAULT_PER_PAGE, TabEmpty, TabSkeletonRows } from '../../../../Vendors/companyTabUi';
import VersionSidebarV2 from './VersionSidebarV2';
import InhouseAppCard from './InhouseAppCard';

// Server-paginated in-house apps (application owned by this company), each row
// carrying its embedded certifications — no full-list fetch.
const inhouseFetcher = (id: number | string, params?: CompanyTabListParams) =>
  getCompanyApps(id, { ...params, kind: 'inhouse' });

type InhouseApplicationsCardsProps = {
  company: Company;
  userCanAdminCompany: boolean;
  companyCallback: any;
  reloadToken?: number;
};

const InhouseApplicationsCards = (props: InhouseApplicationsCardsProps) => {
  const { company, userCanAdminCompany, companyCallback, reloadToken } = props;
  const dispatch = useAppDispatch();
  const { documents } = useAppSelector((state) => state.serviceCategories);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PER_PAGE);
  const { items: applications, count, loading, refetch } = useCompanyResource<CompanyApplication>(
    inhouseFetcher,
    company.id,
    page,
    pageSize,
  );

  // Re-fetch this page when the tab-level add modal reports a change.
  useEffect(() => {
    if (reloadToken) refetch();
  }, [reloadToken]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedCompanyApp, setSelectedCompanyApp] = useState<CompanyApplication | undefined>(undefined);
  const [selectedApp, setSelectedApp] = useState<Application | undefined>(undefined);
  const [selectedVersion, setSelectedVersion] = useState<Version | undefined>(undefined);

  // Keep the parent in sync (header/labels) and refetch this page after a change.
  const handleChange = useCallback((updatedCompany: Company) => {
    companyCallback(updatedCompany);
    refetch();
  }, [companyCallback, refetch]);

  const handleVersionRowClick = useCallback((companyApp: CompanyApplication, app: Application, version: Version) => {
    setSelectedCompanyApp(companyApp);
    setSelectedApp(app);
    setSelectedVersion(version);
    setIsSidebarOpen(true);
    // The sidebar exposes edit, whose form needs services/sites — prefetch now.
  }, []);

  const handleSidebarClose = useCallback(() => {
    setIsSidebarOpen(false);
    setSelectedApp(undefined);
    setSelectedVersion(undefined);
  }, []);

  const handleSidebarEditSave = useCallback((comp: Company, companyApp: CompanyApplication, type: 'edit' | 'delete') => {
    handleChange(comp);
    if (type !== 'delete') {
      setSelectedCompanyApp(companyApp);
      setSelectedApp(companyApp.application);
    } else {
      setIsSidebarOpen(false);
    }
  }, [handleChange]);

  // Prefetch config-guideline (hardening) documents for the apps on this page.
  useEffect(() => {
    applications
      .filter((ca) => ca.application.hardeningDocuments && ca.application.hardeningDocuments.length > 0)
      .forEach((ca) =>
        (ca.application.hardeningDocuments as HardeningDocument[])?.forEach((hd) => {
          if (!documents.find((doc) => doc.id === hd.document)) dispatch(fetchDocument(hd.document));
        }),
      );
  }, [applications]);

  return (
    <Box>
      <Typography variant='h6' sx={{ m: '0 -16px 0px -16px', background: grey[300], padding: '.5em 1.5em' }}>
        In-house Developed Application
      </Typography>
      <DataRow header>
        <DataCell xs={1}>&nbsp;</DataCell>
        <DataCell xs={1} textAlign='center'>Shield</DataCell>
        <DataCell xs={3}>Name</DataCell>
        <DataCell xs={2}>Sites</DataCell>
        <DataCell xs={2}>Services</DataCell>
        <DataCell xs={2}>Application Types</DataCell>
        <DataCell xs={1}>&nbsp;</DataCell>
      </DataRow>

      {loading && <TabSkeletonRows rows={4} columns={[1, 1, 3, 2, 2, 2, 1]} />}

      {!loading && applications.length === 0 && <TabEmpty label='No in-house applications' />}

      {!loading && applications.map((companyApplication: CompanyApplication) => (
        <InhouseAppCard
          key={companyApplication.id}
          company={company}
          companyApplication={companyApplication}
          userCanAdminCompany={userCanAdminCompany}
          companySuccessCallback={handleChange}
          onVersionRowClick={handleVersionRowClick}
        />
      ))}

      <PaginationBar variant='inline' count={count} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />

      <VersionSidebarV2
        isOpen={isSidebarOpen}
        company={company}
        companyApp={selectedCompanyApp}
        application={selectedApp}
        version={selectedVersion}
        onClose={handleSidebarClose}
        userCanAdminCompany={userCanAdminCompany}
        companySuccessCallback={handleSidebarEditSave}
      />
    </Box>
  );
};

export default InhouseApplicationsCards;
